import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Appointment,
  AppointmentStatus,
  Doctor,
  Laboratory,
  Patient,
  TreatmentInProgress,
  TreatmentTemplate
} from './types';
import { getEffectiveHc } from '../../utils/patientHc';
import { getProfilePictureUrl } from '../../utils/profilePicture';
import { useTheme } from '../../theme/ThemeContext';
import { BolivianDateInput, BolivianTimeInput } from '../common/BolivianDateInput';
import { ModalBackdrop } from '../common/ModalBackdrop';
import { AppointmentOperationalModal } from './AppointmentOperationalModal';
import { DEFAULT_APPOINTMENT_REASON_VALUE, isDefaultAppointmentReasonName } from './appointmentReason';
import { isNewPatientStatus } from './reservationFee';
import './AppointmentModal.css';

export interface AppointmentFormState {
  patientId: string;
  doctorId: string;
  secondDoctorId?: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: string;
  notes: string;
  color: string;
  marginColor: string;
  room?: string;
  amountPaid: string;
  paymentMethod?: string;
  treatmentId: string;
  treatmentIds?: string[];
  treatmentTemplateId: string;
  treatmentTemplateSelections: { treatmentTemplateId: string; price: string }[];
  customReason: string;
  newTreatmentName?: string;
  newTreatmentCost?: string;
  newPatientName?: string;
  newPatientContact?: string;
  newPatientNotes?: string;
  isNoShow: boolean;
  status: string;
  isAllDay?: boolean;
  chargeAmount?: string;
  reservationFeeAmount?: string;
  reservationFeePaid?: boolean;
  reservationFeePaymentMethod?: string;
  treatedTeeth?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onDelete: () => void;
  form: AppointmentFormState;
  setForm: React.Dispatch<React.SetStateAction<AppointmentFormState>>;
  editingAppointment: Appointment | null;
  patients: Patient[];
  doctors: Doctor[];
  patientTreatments: TreatmentInProgress[];
  treatmentsLoading: boolean;
  treatmentsError?: string | null;
  treatmentTemplates: TreatmentTemplate[];
  laboratories?: Laboratory[];
  treatmentTemplatesLoading: boolean;
  treatmentTemplatesError?: string | null;
  onSaveDraft?: () => Promise<void>;
  onReservationFeePayment?: (data: { amount: number; date: string; method: string; notes?: string }) => Promise<void>;
  onReservationFeeUpdate?: (amount: number) => Promise<void>;
  onReservationFeeRemove?: () => Promise<void>;
  onReservationFeeRefund?: (data: { amount: number; date: string; method: string; notes?: string }) => Promise<void>;
  canEditPayments?: boolean;
  canViewPayments?: boolean;
  onOpenPatientProfile?: (patientId: number) => void;
  onOpenTreatmentWorkspace?: (treatmentId: number, patientId?: number | null) => void;
  token?: string;
  onRefreshAppointment?: () => Promise<void>;
  onStatusChanged?: (appointmentId: number, status: AppointmentStatus) => void;
  canEditAppointments?: boolean;
}

export const AppointmentModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  form,
  setForm,
  editingAppointment,
  patients,
  doctors,
  patientTreatments,
  treatmentsLoading,
  treatmentsError,
  treatmentTemplates,
  laboratories = [],
  treatmentTemplatesLoading,
  treatmentTemplatesError,
  onSaveDraft,
  onReservationFeePayment,
  onReservationFeeUpdate,
  onReservationFeeRemove,
  onReservationFeeRefund,
  canEditPayments,
  canViewPayments,
  onOpenPatientProfile,
  onOpenTreatmentWorkspace,
  token = '',
  onRefreshAppointment,
  onStatusChanged,
  canEditAppointments
}) => {
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();
  const [patientSearch, setPatientSearch] = useState('');
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (form.patientId === '__new__') {
      setPatientSearch(form.newPatientName || t('calendar.modal.newPatientIntake', 'New patient'));
      return;
    }
    const selected = patients.find(patient => String(patient.id) === form.patientId);
    setPatientSearch(selected?.presentedName || selected?.name || '');
    setShowMore(false);
  }, [form.newPatientName, form.patientId, open, patients, t]);

  if (!open) return null;

  if (editingAppointment) {
    return (
      <AppointmentOperationalModal
        open={open}
        token={token}
        appointment={editingAppointment}
        form={form}
        setForm={setForm}
        doctors={doctors}
        laboratories={laboratories}
        treatmentTemplates={treatmentTemplates}
        onClose={onClose}
        onDelete={onDelete}
        onSaveDraft={onSaveDraft}
        onOpenPatientProfile={onOpenPatientProfile}
        onOpenTreatmentWorkspace={onOpenTreatmentWorkspace}
        onRefreshAppointment={onRefreshAppointment}
        onStatusChanged={onStatusChanged}
        canEditAppointments={canEditAppointments}
        canEditPayments={canEditPayments}
        canViewPayments={canViewPayments}
        onReservationFeePayment={onReservationFeePayment}
        onReservationFeeUpdate={onReservationFeeUpdate}
        onReservationFeeRemove={onReservationFeeRemove}
        onReservationFeeRefund={onReservationFeeRefund}
      />
    );
  }

  const displayTemplateName = (template: TreatmentTemplate) =>
    i18n?.language?.startsWith('es') ? template.nameEs || template.name : template.name;
  const selectedPatient = patients.find(patient => String(patient.id) === form.patientId);
  const selectedPatientName = selectedPatient?.presentedName || selectedPatient?.name || '';
  const selectedPatientPictureUrl = getProfilePictureUrl(selectedPatient?.profilePicturePath);
  const selectedPatientInitials = selectedPatientName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || '?';
  const isNewPatient = form.patientId === '__new__';
  const isReservationPatient = isNewPatient || isNewPatientStatus(selectedPatient?.status);
  const canConfigureReservationFee = isNewPatient || !!selectedPatient;
  const reservationBalance = Math.max(0, Number(
    form.reservationFeeAmount ?? (isReservationPatient ? '50' : '0')
  ));
  const reservationFeeEnabled = reservationBalance > 0.005;
  const reservationPaid = false;
  const filteredPatients = patients.filter(patient => {
    const query = patientSearch.trim().toLowerCase();
    if (!query) return true;
    return [patient.name, patient.presentedName, getEffectiveHc(patient.hc)]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query));
  }).slice(0, 30);

  const selectedTemplateIsConsultation = treatmentTemplates.some(template =>
    String(template.id) === form.treatmentTemplateId &&
    (isDefaultAppointmentReasonName(template.name) || isDefaultAppointmentReasonName(template.nameEs)));
  const reasonValue = form.treatmentId
    ? `treatment:${form.treatmentId}`
    : form.treatmentTemplateId === DEFAULT_APPOINTMENT_REASON_VALUE || selectedTemplateIsConsultation
      ? 'consultation'
    : form.treatmentTemplateId && form.treatmentTemplateId !== '__other__'
      ? `template:${form.treatmentTemplateId}`
      : form.treatmentTemplateId === '__other__' || form.customReason
        ? 'other'
        : '';

  const changeReason = (value: string) => {
    if (value.startsWith('treatment:')) {
      const id = value.slice('treatment:'.length);
      setForm({ ...form, treatmentId: id, treatmentIds: [], treatmentTemplateId: '', treatmentTemplateSelections: [], customReason: '' });
      return;
    }
    if (value.startsWith('template:')) {
      const id = value.slice('template:'.length);
      const template = treatmentTemplates.find(item => String(item.id) === id);
      setForm({
        ...form,
        treatmentId: '',
        treatmentIds: [],
        treatmentTemplateId: id,
        treatmentTemplateSelections: [],
        customReason: '',
        duration: template?.expectedDurationMinutes ? String(template.expectedDurationMinutes) : form.duration
      });
      return;
    }
    if (value === 'consultation') {
      setForm({
        ...form,
        treatmentId: '',
        treatmentIds: [],
        treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
        treatmentTemplateSelections: [],
        customReason: t('calendar.modal.defaultConsultationReason', 'Consultation')
      });
      return;
    }
    setForm({ ...form, treatmentId: '', treatmentIds: [], treatmentTemplateId: value === 'other' ? '__other__' : '', treatmentTemplateSelections: [], customReason: value === 'other' ? form.customReason : '' });
  };

  const choosePatient = (patient: Patient) => {
    setForm({
      ...form,
      patientId: String(patient.id),
      newPatientName: '',
      newPatientContact: '',
      newPatientNotes: '',
      treatmentId: '',
      treatmentIds: [],
      treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
      treatmentTemplateSelections: [],
      customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
      reservationFeeAmount: isNewPatientStatus(patient.status) ? '50' : '',
      reservationFeePaid: false,
      reservationFeePaymentMethod: 'Cash'
    });
    setPatientSearch(patient.presentedName || patient.name);
    setPatientDropdownOpen(false);
  };

  const chooseNewPatient = () => {
    setForm({
      ...form,
      patientId: '__new__',
      treatmentId: '',
      treatmentIds: [],
      treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
      treatmentTemplateSelections: [],
      customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
      reservationFeeAmount: '50',
      reservationFeePaid: false,
      reservationFeePaymentMethod: 'Cash'
    });
    setPatientSearch(t('calendar.modal.newPatientIntake', 'New patient'));
    setPatientDropdownOpen(false);
  };

  const input: React.CSSProperties = {
    padding: '11px 12px', border: `1px solid ${tokens.app.inputBorder}`, borderRadius: 6,
    background: tokens.app.input, color: tokens.app.text, colorScheme: tokens.isLight ? 'light' : 'dark', minWidth: 0, boxSizing: 'border-box'
  };
  const label: React.CSSProperties = { display: 'grid', gap: 6, color: tokens.app.muted, fontSize: 13, fontWeight: 700 };
  const button: React.CSSProperties = { padding: '11px 16px', border: 0, borderRadius: 6, background: tokens.app.primary, color: tokens.app.primaryText, cursor: 'pointer', fontWeight: 700 };
  const secondary: React.CSSProperties = { ...button, background: tokens.app.surfaceAlt, color: tokens.app.text, border: `1px solid ${tokens.app.border}` };
  const reasonOptions = patientTreatments.filter(item => Number(item.due || 0) >= 0);

  return (
    <ModalBackdrop className="calendar-modal-overlay" onDismiss={onClose}>
      <div data-testid="appointment-create-modal" className="calendar-modal-shell" style={{ background: tokens.app.surface, color: tokens.app.text, border: `1px solid ${tokens.app.border}`, maxWidth: 820 }} onClick={event => event.stopPropagation()}>
        <div className="calendar-modal-header responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>{t('calendar.modal.newAppointment', 'New appointment')}</h2>
            <div style={{ color: tokens.app.muted, marginTop: 4 }}>{t('calendar.modal.simpleAppointmentHelp', 'Patient, time, reason, save.')}</div>
          </div>
          <button type="button" onClick={onClose} aria-label={t('calendar.modal.cancel', 'Cancel')} style={secondary}>x</button>
        </div>

        <form className="calendar-modal-body" onSubmit={onSubmit} style={{ display: 'grid', gap: 14 }}>
          <label style={label}>{t('calendar.modal.patient', 'Patient')}
            <div style={{ position: 'relative' }}>
              <input
                data-testid="appointment-patient-search"
                style={{ ...input, width: '100%' }}
                value={patientSearch}
                placeholder={t('calendar.modal.selectPatient', 'Search patient')}
                onFocus={() => setPatientDropdownOpen(true)}
                onBlur={() => window.setTimeout(() => setPatientDropdownOpen(false), 150)}
                onChange={event => {
                  setPatientSearch(event.target.value);
                  setPatientDropdownOpen(true);
                  if (form.patientId) setForm({
                    ...form,
                    patientId: '',
                    treatmentId: '',
                    treatmentIds: [],
                    treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
                    customReason: t('calendar.modal.defaultConsultationReason', 'Consultation')
                  });
                }}
                required={!isNewPatient}
              />
              {patientDropdownOpen && <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, maxHeight: 240, overflowY: 'auto', background: tokens.app.surface, border: `1px solid ${tokens.app.border}`, borderRadius: 6, boxShadow: tokens.app.shadow }}>
                <button type="button" onMouseDown={event => event.preventDefault()} onClick={chooseNewPatient} style={{ width: '100%', padding: '10px 12px', border: 0, textAlign: 'left', background: tokens.app.activeBg, color: tokens.app.activeText, cursor: 'pointer', fontWeight: 700 }}>+ {t('calendar.modal.newPatient', 'New patient')}</button>
                {filteredPatients.map(patient => <button key={patient.id} type="button" onMouseDown={event => event.preventDefault()} onClick={() => choosePatient(patient)} style={{ width: '100%', padding: '10px 12px', border: 0, borderTop: `1px solid ${tokens.app.border}`, textAlign: 'left', background: 'transparent', color: tokens.app.text, cursor: 'pointer' }}>{patient.presentedName || patient.name}{getEffectiveHc(patient.hc) ? ` · ${getEffectiveHc(patient.hc)}` : ''}</button>)}
                {filteredPatients.length === 0 && <div style={{ padding: 10, color: tokens.app.muted }}>{t('calendar.modal.noPatientsFound', 'No patients found')}</div>}
              </div>}
            </div>
          </label>

          {isNewPatient && <section className="responsive-grid" style={{ padding: 12, border: `1px solid ${tokens.app.border}`, borderRadius: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, background: tokens.app.surfaceAlt }}>
            <label style={label}>{t('calendar.modal.prospectName', 'Full name')}<input style={input} value={form.newPatientName || ''} onChange={event => { setPatientSearch(event.target.value); setForm({ ...form, newPatientName: event.target.value }); }} required /></label>
            <label style={label}>{t('calendar.modal.contactInfo', 'Phone')}<input style={input} type="tel" value={form.newPatientContact || ''} onChange={event => setForm({ ...form, newPatientContact: event.target.value })} /></label>
            <div style={{ gridColumn: '1 / -1', color: tokens.app.muted, fontSize: 12 }}>{t('calendar.modal.hcAssignedAtCompletion', 'HC will be assigned when appointment is completed.')}</div>
          </section>}

          {selectedPatient && <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 10, color: tokens.app.muted, fontSize: 13 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', position: 'relative', flex: '0 0 38px', display: 'grid', placeItems: 'center', background: tokens.app.activeBg, color: tokens.app.activeText, border: `1px solid ${tokens.app.border}`, fontWeight: 800 }}>
              <span>{selectedPatientInitials}</span>
              {selectedPatientPictureUrl && <img src={selectedPatientPictureUrl} alt={`${selectedPatientName} ${t('calendar.operational.patientPhoto', 'profile picture')}`} onError={event => { event.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
            </div>
            <span>{selectedPatientName}{getEffectiveHc(selectedPatient.hc) ? ` · HC ${getEffectiveHc(selectedPatient.hc)}` : ''}</span>
          </div>}

          {canConfigureReservationFee && <section style={{ padding: 12, border: `1px solid ${tokens.app.border}`, borderRadius: 8, display: 'grid', gap: 10, background: tokens.app.surfaceAlt }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <strong>{t('calendar.modal.reservationFee', 'Reservation fee')}</strong>
                <div style={{ color: tokens.app.muted, fontSize: 12, marginTop: 3 }}>
                  {reservationFeeEnabled
                    ? t('calendar.modal.reservationPendingAmount', 'Reservation pending: Bs {{amount}}').replace('{{amount}}', reservationBalance.toFixed(2))
                    : t('calendar.modal.reservationFeeNotConfigured', 'No reservation fee configured.')}
                </div>
              </div>
              {canEditPayments && <button type="button" style={{ ...secondary, color: reservationFeeEnabled ? '#dc2626' : secondary.color }} onClick={() => setForm({
                ...form,
                reservationFeeAmount: reservationFeeEnabled ? '' : '50',
                reservationFeePaid: false
              })}>{reservationFeeEnabled ? t('calendar.modal.removeReservationFee', 'Remove reservation fee') : t('calendar.modal.addReservationFee', 'Add reservation fee')}</button>}
            </div>
            {reservationFeeEnabled && <label style={label}>{t('calendar.modal.reservationFeeAmount', 'Reservation fee amount (Bs)')}
              <input style={input} type="number" min="0.01" step="0.01" value={form.reservationFeeAmount || ''} onChange={event => setForm({ ...form, reservationFeeAmount: event.target.value, reservationFeePaid: false })} required />
            </label>}
            {canEditPayments && reservationFeeEnabled && isReservationPatient && !reservationPaid && <>
              <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.app.text, fontSize: 13, fontWeight: 700 }}>
                <input
                  type="checkbox"
                  checked={!!form.reservationFeePaid}
                  onChange={event => setForm({ ...form, reservationFeePaid: event.target.checked })}
                />
                {t('calendar.modal.markReservationPaid', 'Bs {{amount}} received').replace('{{amount}}', reservationBalance.toFixed(2))}
              </label>
              {form.reservationFeePaid && <label style={label}>{t('calendar.modal.reservationPaymentMethod', 'Payment method')}
                <select style={input} value={form.reservationFeePaymentMethod || 'Cash'} onChange={event => setForm({ ...form, reservationFeePaymentMethod: event.target.value })} required>
                  {['Cash', 'QR', 'Card', 'Transfer', 'Other'].map(method => <option key={method} value={method}>{t(`calendar.operational.paymentMethods.${method.toLowerCase()}`, method)}</option>)}
                </select>
              </label>}
            </>}
          </section>}

          <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <label style={label}>{t('calendar.modal.doctor', 'Doctor')}
              <select data-testid="appointment-doctor" style={input} value={form.doctorId} onChange={event => {
                const doctor = doctors.find(item => String(item.id) === event.target.value);
                setForm({ ...form, doctorId: event.target.value, color: doctor?.color || form.color });
              }} required>
                <option value="">{t('calendar.modal.selectDoctor', 'Select doctor')}</option>
                {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
              </select>
            </label>
            <label style={label}>{t('calendar.modal.date', 'Date')}<BolivianDateInput data-testid="appointment-date" style={input} value={form.date} onChange={date => setForm({ ...form, date })} required /></label>
            {!form.isAllDay && <label style={label}>{t('calendar.modal.startTime', 'Start time')}<BolivianTimeInput style={input} value={form.startTime} onChange={startTime => setForm({ ...form, startTime })} required /></label>}
            {!form.isAllDay && <label style={label}>{t('calendar.modal.endTime', 'End time')}<BolivianTimeInput style={input} value={form.endTime} onChange={endTime => setForm({ ...form, endTime })} required /></label>}
            <label style={label}>{t('calendar.modal.room', 'Room')}
              <select style={input} value={form.room || ''} onChange={event => setForm({ ...form, room: event.target.value })}>
                <option value="">{t('calendar.modal.selectRoom', 'Select room')}</option>{[1, 2, 3, 4, 5].map(room => <option key={room} value={room}>{room}</option>)}
              </select>
            </label>
          </div>

          <label style={label}>{t('calendar.modal.reason', 'Reason')}
            <select style={input} value={reasonValue} onChange={event => changeReason(event.target.value)} required>
              <option value="">{treatmentTemplatesLoading || treatmentsLoading ? t('common.loading', 'Loading...') : t('calendar.modal.selectReason', 'Select reason')}</option>
              <option value="consultation">{t('calendar.modal.defaultConsultationReason', 'Consultation')}</option>
              {!isNewPatient && reasonOptions.length > 0 && <optgroup label={t('calendar.modal.existingTreatments', 'Existing treatments')}>{reasonOptions.map(item => <option key={item.id} value={`treatment:${item.id}`}>{item.name}</option>)}</optgroup>}
              <optgroup label={t('calendar.modal.catalogReasons', 'Treatment catalog')}>{treatmentTemplates.filter(template => !isDefaultAppointmentReasonName(template.name) && !isDefaultAppointmentReasonName(template.nameEs)).map(template => <option key={template.id} value={`template:${template.id}`}>{displayTemplateName(template)}</option>)}</optgroup>
              <option value="other">{t('calendar.modal.otherReason', 'Other')}</option>
            </select>
          </label>
          {(treatmentsError || treatmentTemplatesError) && <div role="alert" style={{ color: '#ef4444', fontSize: 12 }}>{treatmentsError || treatmentTemplatesError}</div>}
          {reasonValue === 'other' && <label style={label}>{t('calendar.modal.reason', 'Reason')}<input style={input} value={form.customReason} onChange={event => setForm({ ...form, customReason: event.target.value })} required /></label>}

          <label style={label}>{t('calendar.modal.appointmentNotes', 'Notes')}<textarea data-testid="appointment-notes" style={{ ...input, minHeight: 80 }} value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} /></label>

          <button type="button" style={{ ...secondary, justifySelf: 'start' }} onClick={() => setShowMore(value => !value)}>{t('calendar.modal.moreOptions', 'More options')}</button>
          {showMore && <label style={{ ...label, maxWidth: 320 }}>{t('calendar.modal.secondDoctor', 'Second doctor')}
            <select style={input} value={form.secondDoctorId || ''} onChange={event => setForm({ ...form, secondDoctorId: event.target.value })}>
              <option value="">-</option>{doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
            </select>
          </label>}

          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" style={secondary} onClick={onClose}>{t('calendar.modal.cancel', 'Cancel')}</button>
            <button data-testid="appointment-submit" type="submit" style={button}>{t('calendar.modal.createAppointment', 'Create appointment')}</button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
};
